import * as Icons from '@ant-design/icons';
import { AssessmentAttemptLogModal } from 'components/modal/evaluations/assessment/AssessmentAttemptLogModal';
import { AssessmentEditScoreModal } from 'components/modal/evaluations/assessment/AssessmentEditScoreModal';
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Row,
  Space,
  Tag,
  Typography
} from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import { IAssessmentAttemptShow } from 'interfaces/assessments';
import { PageHeader } from 'components/professor_area/PageHeader';
import { QUESTION_STATUSES_MAP } from 'services/questions';
import { ShowFieldData } from 'components/ShowFieldData';
import { ShowLayout } from 'components/layout/layout/ShowLayout';
import { TagField } from '@refinedev/antd';
import { formatDate } from 'services/date';
import { tagColorByStatus } from '../../../utils/statusColorMapping';
import { translateAssessmentAttemptStatus } from 'services/assessments';
import { useMemo } from 'react';
import { useModal, useShow } from '@refinedev/core';

const { Title, Text } = Typography;

export const AssessmentAttemptShow = () => {
  const { queryResult } = useShow<IAssessmentAttemptShow>();
  const logModal = useModal();
  const editScoreModal = useModal();

  const attempt = queryResult?.data?.data;

  const onViewLogModal = () => {
    logModal.show();
  };

  const onEditScoreModal = () => {
    editScoreModal.show();
  };

  const questionsAnswersBytType = useMemo(() => {
    const answersByType = {
      blank: 0,
      wrong: 0,
      correct: 0,
      nullified: 0,
      not_evaluated: 0
    };

    attempt?.questions.map(question => {
      if (question.nullified_feedback) {
        return answersByType.nullified++;
      }

      switch (question.status) {
        case 'wrong': {
          answersByType.wrong++;
          break;
        }

        case 'blank': {
          answersByType.blank++;
          break;
        }

        case 'correct': {
          answersByType.correct++;
          break;
        }

        case 'not_evaluated': {
          answersByType.not_evaluated++;
          break;
        }

        default: {
          break;
        }
      }
    });

    return answersByType;
  }, [attempt?.questions]);

  return (
    <ShowLayout>
      <PageHeader
        title={attempt?.assessment.name}
        icon={FileDoneOutlined}
        meta={
          <Flex>
            <Tag color={tagColorByStatus(attempt?.status || '')}>
              {translateAssessmentAttemptStatus(attempt?.status ?? '')}
            </Tag>
          </Flex>
        }
        actions={
          <Space>
            <Button
              icon={<Icons.EyeOutlined />}
              onClick={onEditScoreModal}
              disabled={
                !!attempt?.status &&
                !['passed', 'failed'].includes(attempt?.status)
              }
            >
              Editar nota
            </Button>

            <Button icon={<Icons.EyeOutlined />} onClick={onViewLogModal}>
              Visualizar alterações
            </Button>
          </Space>
        }
      />

      <Card>
        <Space direction="vertical" size="middle" className="w-full">
          <ShowFieldData
            label="Nome da Avaliação"
            value={attempt?.assessment.name}
          />

          <Row gutter={[24, 16]} className="w-full">
            <Col span={6}>
              <ShowFieldData
                label="Tipo de curso"
                value={
                  attempt?.assessment.parent_type === 'Course'
                    ? 'Curso'
                    : 'Disciplina'
                }
              />
            </Col>

            <Col>
              <ShowFieldData
                label={
                  attempt?.assessment.parent_type === 'Course'
                    ? 'Nome do curso'
                    : 'Nome da disciplina'
                }
                value={attempt?.assessment.course_name}
              />
            </Col>
          </Row>

          <Row gutter={24} className="w-full">
            <Col span={6}>
              <ShowFieldData
                label="Iniciado em"
                value={
                  attempt?.created_at
                    ? formatDate(attempt.created_at, 'DD/MM/YYYY HH:mm')
                    : '--'
                }
              />
            </Col>
            <Col>
              <ShowFieldData
                label="Finalizado em"
                value={
                  attempt?.completed_at
                    ? formatDate(attempt.completed_at, 'DD/MM/YYYY HH:mm')
                    : '--'
                }
              />
            </Col>
          </Row>

          <Row gutter={24} className="w-full">
            <Col span={6}>
              <ShowFieldData label="Nota" value={attempt?.score ?? '--'} />
            </Col>
            <Col span={6}>
              <ShowFieldData
                label="Aluno"
                value={attempt?.user?.full_name_or_name ?? '--'}
              />
            </Col>
          </Row>
        </Space>

        <Divider />

        <Space direction="vertical" size="small" className="w-full mb-4">
          <Title level={5} className="!mb-0">
            Questões
          </Title>
          <Space size="middle">
            <Typography.Text style={{ fontSize: 12 }}>
              Corretas: {questionsAnswersBytType.correct}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12 }}>
              Erradas: {questionsAnswersBytType.wrong}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12 }}>
              Em branco: {questionsAnswersBytType.blank}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12 }}>
              Anuladas: {questionsAnswersBytType.nullified}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12 }}>
              Não avaliadas: {questionsAnswersBytType.not_evaluated}
            </Typography.Text>
          </Space>
        </Space>

        <Space size="middle" direction="vertical" className="w-full">
          {attempt?.questions.map((question, index) => {
            const questionStatus = QUESTION_STATUSES_MAP[
              question.nullified_feedback ? 'nullified' : question.status
            ] ?? {
              label: question.status,
              color: 'default'
            };

            return (
              <div key={question.id} className="flex flex-col w-full gap-1">
                <div className="flex items-center gap-1">
                  <Text>{index + 1} - </Text>
                  <TagField
                    color={questionStatus.color}
                    value={questionStatus.label}
                  />
                </div>

                <div
                  dangerouslySetInnerHTML={{
                    __html: question.statement
                  }}
                />
              </div>
            );
          })}
        </Space>

        {attempt?.id && logModal.visible && (
          <AssessmentAttemptLogModal attemptId={attempt.id} modal={logModal} />
        )}

        {attempt?.id && editScoreModal.visible && (
          <AssessmentEditScoreModal attempt={attempt} modal={editScoreModal} />
        )}
      </Card>
    </ShowLayout>
  );
};
